import { FC } from 'react';
import { InvestorProfile } from '@/types/index';

interface Props {
  profile: Partial<InvestorProfile>;
  onChange: (data: Partial<InvestorProfile>) => void;
}

export const Step3MoyensFinanciers: FC<Props> = ({ profile, onChange }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Fonds propres disponibles <span className="text-gray-400">(CHF)</span>
          </label>
          <input
            type="number"
            step="10000"
            min="0"
            value={profile.fondsPropresCHF ?? ''}
            onChange={e => onChange({ fondsPropresCHF: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="ex: 200000"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">Capital disponible pour l'apport</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Capacité d'emprunt totale <span className="text-gray-400">(CHF)</span>
          </label>
          <input
            type="number"
            step="50000"
            min="0"
            value={profile.capaciteEmprunt ?? ''}
            onChange={e => onChange({ capaciteEmprunt: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="ex: 800000"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">Estimée par votre banque</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Apport propre <span className="text-gray-400">(%)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="10"
              max="50"
              step="5"
              value={profile.equityRate ?? 20}
              onChange={e => onChange({ equityRate: parseInt(e.target.value) })}
              className="flex-1 h-2 rounded-full accent-blue-500"
            />
            <span className="w-12 text-right font-semibold text-sm text-gray-900 dark:text-gray-100">
              {profile.equityRate ?? 20}%
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>10%</span><span>50%</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Budget travaux max <span className="text-gray-400">(CHF)</span>
          </label>
          <input
            type="number"
            step="10000"
            min="0"
            value={profile.budgetRenovMax ?? ''}
            onChange={e => onChange({ budgetRenovMax: e.target.value ? parseFloat(e.target.value) : null })}
            placeholder="ex: 100000"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Projets simultanés maximum
        </label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => onChange({ projetSimultanes: n })}
              className={`w-10 h-10 rounded-full border-2 font-semibold text-sm transition-all
                ${(profile.projetSimultanes ?? 1) === n
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-300'}`}
            >
              {n}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">Combien de biens peut-on financer en même temps ?</p>
      </div>

      {profile.fondsPropresCHF && profile.equityRate && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
            Estimation budget d'achat
          </p>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">
            CHF {Math.round(profile.fondsPropresCHF / (profile.equityRate / 100)).toLocaleString('de-CH')}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
            {profile.fondsPropresCHF.toLocaleString('de-CH')} CHF / {profile.equityRate}% apport
          </p>
        </div>
      )}
    </div>
  );
};
